import React, { useState, useEffect } from 'react';
import { collection, getDocs } from 'firebase/firestore';
import { db, auth } from './firebase';

// ─────────────────────────────────────────────────────────────────────────────
// AdminImpersonate
//
// PURPOSE:
//   Allows an admin to open the franchisee app logged in as any franchisee.
//   Uses a secure Cloud Function to generate a short-lived custom token.
//   The franchisee app shows a red banner so you always know you're impersonating.
//
// HOW TO ADD TO AdminDashboard.js:
//   1. import AdminImpersonate from './AdminImpersonate';
//   2. if (currentView === 'impersonate') {
//        return <AdminImpersonate onClose={() => setCurrentView('dashboard')} />;
//      }
//   3. <button onClick={() => setCurrentView('impersonate')}>
//        🔑 Login as Franchisee
//      </button>
//
// CLOUD FUNCTION URL:
//   Update FUNCTION_URL below with your actual project region + function name.
//   Find it in Firebase Console → Functions → generateImpersonationToken → URL
// ─────────────────────────────────────────────────────────────────────────────

// ⚠️ UPDATE THIS to your actual Cloud Function URL
const FUNCTION_URL = 'https://generateimpersonationtoken-pakkytdqla-uc.a.run.app';

// The franchisee app URL — update if different
const FRANCHISEE_APP_URL = 'https://babs-2ee3f.web.app';

export default function AdminImpersonate({ onClose }) {
  const [franchisees, setFranchisees]   = useState([]);
  const [loading, setLoading]           = useState(true);
  const [selected, setSelected]         = useState('');
  const [selectedName, setSelectedName] = useState('');
  const [working, setWorking]           = useState(false);
  const [error, setError]               = useState('');

  useEffect(() => {
    (async () => {
      try {
        const snap = await getDocs(collection(db, 'Franchisees'));
        const list = snap.docs
          .map(d => ({ id: d.id, name: d.data().name || d.id }))
          .sort((a, b) => a.name.localeCompare(b.name));
        setFranchisees(list);
      } catch (e) {
        setError('Could not load franchisees: ' + e.message);
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  const handleLogin = async () => {
    if (!selected) return;
    setWorking(true);
    setError('');

    try {
      // Get the admin's current ID token to prove we're an admin
      const idToken = await auth.currentUser.getIdToken();

      const response = await fetch(FUNCTION_URL, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${idToken}`
        },
        body: JSON.stringify({
          franchiseeUid: selected,
          franchiseeName: selectedName
        })
      });

      const data = await response.json();

      if (!data.success || !data.token) {
        throw new Error(data.error || 'Failed to generate token');
      }

      // Open franchisee app in new tab with the token in the URL
      const url = `${FRANCHISEE_APP_URL}/?token=${encodeURIComponent(data.token)}&name=${encodeURIComponent(selectedName)}`;
      window.open(url, '_blank');

    } catch (e) {
      console.error('Impersonation error:', e);
      setError('Error: ' + e.message);
    } finally {
      setWorking(false);
    }
  };

  const s = {
    overlay: {
      position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.55)',
      display: 'flex', alignItems: 'center', justifyContent: 'center',
      zIndex: 9999
    },
    box: {
      background: '#fff', borderRadius: '12px', width: '100%',
      maxWidth: '480px', boxShadow: '0 8px 40px rgba(0,0,0,0.25)',
      overflow: 'hidden'
    },
    header: {
      background: 'linear-gradient(135deg,#1a1a6e 0%,#2B4BA0 100%)',
      padding: '18px 24px',
      display: 'flex', alignItems: 'center', justifyContent: 'space-between'
    },
    body: { padding: '24px' },
    warn: {
      background: '#fff3cd', color: '#856404', padding: '12px 16px',
      borderRadius: '8px', marginBottom: '20px',
      border: '1px solid #ffc107', fontSize: '13px', lineHeight: '1.6'
    },
    label: { display: 'block', fontWeight: 600, marginBottom: '6px', color: '#333', fontSize: '14px' },
    select: {
      width: '100%', padding: '11px 12px',
      border: '2px solid #667eea', borderRadius: '8px',
      fontSize: '15px', marginBottom: '20px'
    },
    btn: {
      width: '100%', padding: '13px',
      background: '#c62828', color: '#fff',
      border: 'none', borderRadius: '8px',
      fontSize: '15px', fontWeight: 700, cursor: 'pointer'
    },
    err: {
      background: '#f8d7da', color: '#721c24', padding: '12px',
      borderRadius: '8px', marginTop: '12px',
      border: '1px solid #f5c6cb', fontSize: '13px'
    }
  };

  return (
    <div style={s.overlay}>
      <div style={s.box}>

        <div style={s.header}>
          <h2 style={{ color: '#fff', margin: 0, fontSize: '18px', fontWeight: 700 }}>
            🔑 Login as Franchisee
          </h2>
          <button
            onClick={onClose}
            style={{ background: 'rgba(255,255,255,0.2)', color: '#fff', border: '1px solid rgba(255,255,255,0.4)', borderRadius: '8px', padding: '7px 14px', cursor: 'pointer', fontWeight: 600 }}
          >
            ✕
          </button>
        </div>

        <div style={s.body}>
          <div style={s.warn}>
            ⚠️ <strong>You will open the franchisee app in a new tab, signed in as that franchisee.</strong>
            <br />A red banner will remind you that you are impersonating.
            Any changes you make there are <strong>real and permanent</strong>.
            The session expires after 1 hour.
          </div>

          <label style={s.label}>Select Franchisee</label>
          {loading ? (
            <p style={{ color: '#888' }}>Loading franchisees...</p>
          ) : (
            <select
              style={s.select}
              value={selected}
              onChange={e => {
                setSelected(e.target.value);
                setSelectedName(franchisees.find(f => f.id === e.target.value)?.name || '');
              }}
            >
              <option value=''>-- Select a franchisee --</option>
              {franchisees.map(f => (
                <option key={f.id} value={f.id}>{f.name}</option>
              ))}
            </select>
          )}

          <button
            style={{ ...s.btn, opacity: !selected || working ? 0.6 : 1 }}
            disabled={!selected || working}
            onClick={handleLogin}
          >
            {working ? '⏳ Generating secure token...' : `🔑 Open App as ${selectedName || '...'}`}
          </button>

          {error && <div style={s.err}>{error}</div>}
        </div>
      </div>
    </div>
  );
}